// frontend/src/components/FinanceReport/Accounting/allocations/CommissionRow.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { money } from "./helpers";
import OrderCodeInput from "./OrderCodeInput";

export default function CommissionRow({
  commission: c,
  currenciesById,
  accountsById,
  paymentCurrency,
  isEditing,
  editDraft,
  setEditDraft,
  editError,
  editSaving,
  clientOrders,
  currencies,
  accounts,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onOpenDocs,
}) {
  const navigate = useNavigate();
  const currency = currenciesById[c.currency] || paymentCurrency || null;
  const acc = accountsById[c.account];
  const accLabel =
    c.account_name || acc?.name || (c.account ? `Счёт #${c.account}` : "—");

  const rowClass = isEditing
    ? "border-t bg-yellow-50"
    : "border-t bg-blue-50";

  return (
    <tr className={rowClass}>
      <td className="border p-2 align-top">
        {isEditing ? (
          <input
            type="date"
            className="w-full rounded border px-1 py-0.5 text-xs"
            value={editDraft.date}
            onChange={(e) =>
              setEditDraft((cur) => ({ ...cur, date: e.target.value }))
            }
          />
        ) : c.date ? (
          new Date(`${c.date}T00:00:00`).toLocaleDateString("ru-RU")
        ) : (
          "—"
        )}
      </td>

      <td className="border p-2 align-top">
        <span className="inline-block rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
          Комиссия
        </span>
      </td>

      <td className="border p-2 align-top">
        {isEditing ? (
          <OrderCodeInput
            id={`edit-comm-${c.id}`}
            orders={clientOrders}
            value={editDraft.orderCode}
            onChange={(v) =>
              setEditDraft((cur) => ({ ...cur, orderCode: v }))
            }
          />
        ) : c.order ? (

          <button
            type="button"
            onClick={() => navigate(`/orders/${c.order}`)}
            className="text-blue-700 hover:underline"
          >
            {c.order_code || `#${c.order}`}
          </button>
        ) : (
          "—"
        )}
      </td>

      <td className="border p-2 align-top">
        {isEditing ? (
          <select
            className="w-full rounded border px-1 py-0.5 text-xs"
            value={editDraft.account}
            onChange={(e) =>
              setEditDraft((cur) => ({ ...cur, account: e.target.value }))
            }
          >
            <option value="">— счёт —</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
                {a.currency ? ` (${a.currency})` : ""}
              </option>
            ))}
          </select>
        ) : c.account ? (
          <button
            type="button"
            onClick={() =>
              navigate(`/accounting/company-accounts/${c.account}`)
            }
            className="text-left text-blue-700 hover:underline"
          >
            {accLabel}
          </button>
        ) : (
          "—"
        )}
      </td>

      <td className="border p-2 align-top font-semibold text-blue-900">
        {isEditing ? (
          <div className="flex gap-1">
            <input
              type="number"
              onWheel={(e) => e.currentTarget.blur()}
              min="0.01"
              step="0.01"
              className="w-full rounded border px-1 py-0.5 text-xs"
              value={editDraft.amount}
              onChange={(e) =>
                setEditDraft((cur) => ({ ...cur, amount: e.target.value }))
              }
            />
            <select
              className="w-20 rounded border px-1 py-0.5 text-xs"
              value={editDraft.currency}
              onChange={(e) =>
                setEditDraft((cur) => ({
                  ...cur,
                  currency: e.target.value,
                }))
              }
            >
              <option value="">—</option>
              {currencies.map((cc) => (
                <option key={cc.id} value={cc.id}>
                  {cc.symbol} {cc.code}
                </option>
              ))}
            </select>
          </div>
        ) : (
          money(c.amount, currency)
        )}
      </td>

      <td className="border p-2 align-top text-xs text-slate-500">—</td>

      <td className="border p-2 align-top">
        {isEditing ? (
          <select
            className="w-full rounded border px-1 py-0.5 text-xs"
            value={editDraft.payment_status}
            onChange={(e) =>
              setEditDraft((cur) => ({
                ...cur,
                payment_status: e.target.value,
              }))
            }
          >
            <option value="PENDING">Ожидает</option>
            <option value="PAID">Оплачен</option>
          </select>
        ) : (
          <span
            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
              c.payment_status === "PAID"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-amber-100 text-amber-700"
            }`}
          >
            {c.payment_status === "PAID" ? "Оплачен" : "Ожидает"}
          </span>
        )}
      </td>

      <td className="border p-2 align-top">
        {isEditing ? (
          <input
            className="w-full rounded border px-1 py-0.5 text-xs"
            placeholder="Коммент."
            value={editDraft.comment}
            onChange={(e) =>
              setEditDraft((cur) => ({ ...cur, comment: e.target.value }))
            }
          />
        ) : (
          <span
            className="block max-w-[16rem] truncate text-xs text-slate-600"
            title={c.comment || ""}
          >
            {c.comment || "—"}
          </span>
        )}
      </td>

      <td className="border p-2 align-top">
        {isEditing ? (
          <input
            className="w-full rounded border px-1 py-0.5 text-xs"
            placeholder="Через"
            value={editDraft.payment_agent}
            onChange={(e) =>
              setEditDraft((cur) => ({
                ...cur,
                payment_agent: e.target.value,
              }))
            }
          />
        ) : (
          <span className="text-xs text-slate-600">
            {c.payment_agent || "—"}
          </span>
        )}
      </td>

      <td className="border p-2 align-top">
        <button
          type="button"
          onClick={() => onOpenDocs({ item: c, type: "commission" })}
          className="text-xs text-blue-700 underline hover:text-blue-900"
        >
          📎 ({c.documents?.length || 0})
        </button>
      </td>

      <td className="border p-2 align-top">
        {isEditing ? (
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={onSaveEdit}
              disabled={editSaving}
              className="rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {editSaving ? "…" : "Сохранить"}
            </button>
            <button
              type="button"
              onClick={onCancelEdit}
              disabled={editSaving}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Отмена
            </button>
            {editError && (
              <div className="mt-1 w-full text-xs text-red-600">
                {editError}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={() => onStartEdit(c)}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
              title="Редактировать"
            >
              ✏
            </button>
            <button
              type="button"
              onClick={() => onDelete(c)}
              className="rounded border border-red-200 bg-white px-2 py-1 text-xs text-red-600 hover:bg-red-50"
              title="Удалить"
            >
              🗑
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}